
import { useState, useEffect, forwardRef, useImperativeHandle, useMemo, useCallback, type MouseEvent } from "react";
import axios from 'axios';
import { toast } from 'react-toastify';
import { useCart } from "../../../../contexts/CartContext";
import { useNavigate } from "react-router-dom";
import { ApiService } from "../../../../shared/service/Apiservice";
import { environment } from "../../../../shared/enviroment/environment";
import buildCoinsService from '../../../Profile/service/BuildCoins.service';
import { ProfileService } from '../../../Profile/service/Profile.service';
import { AddressService } from '../../../Profile/service/Address.service';
import { Address } from '../../../Profile/dto/Address.dto';
import NewAddressModal from '../../../Profile/ManageAddresses/NewAddressModal/NewAddressModal';
import CheckoutService from '../../../Checkout/service/Checkout.service';
import { useAuth } from '../../../Auth/AuthContext';
import { cartApiService } from '../../../../services/cart.api.service';
import { LocationService } from '../../../../services/location.api.service';
import CartConfigService from './service/CartConfig.service';
import { CartConfig } from './dto/cart-config.dto';
import { getCartCategoryTimeLimit } from './utils/categoryTimeLimit';

import "./styles/OrderSummary.css";
import PaymentSpinner from "@/shared/components/PaymentSpinner/PaymentSpinner";


interface CartOrderSummaryProps {
  hidePaymentButton?: boolean;
  onPaymentClick?: () => void;
  onAddressChange?: (address: any) => void;
  onPaymentSuccess?: (orderId: string) => void;
}

const CartOrderSummary = forwardRef<{ 
  openPaymentModal: () => void; 
  openAddressModal: () => void; 
  triggerPayment: () => void 
}, CartOrderSummaryProps>(
  ({ hidePaymentButton = false, onPaymentClick, onAddressChange, onPaymentSuccess }, ref) => {
    const navigate = useNavigate();
    const { cartItems, getCartTotal, clearCart } = useCart();
    const { isAuthenticated } = useAuth();
    const subTotal = Math.round(getCartTotal());

    const apiService = useMemo(() => new ApiService(axios.create()), []);
    const addressService = useMemo(() => new AddressService(apiService), [apiService]);

    const [, setUserOrderCount] = useState(0);

    // Fetch cart config when cart items change or on mount
    useEffect(() => {
      const fetchCartConfig = async () => {
        try {
          const configService = new CartConfigService();
          const config = await configService.getCartConfig();
          setCartConfig(config);
        } catch (error) {
          console.error('Error fetching cart config:', error);
          setCartConfig(null);
        }
      };

      fetchCartConfig();
    }, [cartItems]);

    // Fetch user order count
    useEffect(() => {
      const fetchUserOrderCount = async () => {
        if (!isAuthenticated) {
          setUserOrderCount(0);
          return;
        }

        try {
          const apiService = new ApiService(axios.create());
          const profileService = new ProfileService(apiService);
          const response = await profileService.GetUserOrder();

          if (response.success && response.orders) {
            const orderCount = response.orders.length;
            setUserOrderCount(orderCount);
          } else {
            setUserOrderCount(0);
          }
        } catch (error) {
          setUserOrderCount(0);
        }
      };

      fetchUserOrderCount();
    }, [isAuthenticated, subTotal]);

    const [promoCode, setPromoCode] = useState('');
    const [discount, setDiscount] = useState(0);
    const [appliedCoupon, setAppliedCoupon] = useState<string | null>(null);
    const [promoError, setPromoError] = useState('');
    const [promoLoading, setPromoLoading] = useState(false);
    const [buildCoinsBalance, setBuildCoinsBalance] = useState(0);
    const [buildCoinsToUse, setBuildCoinsToUse] = useState(0);
    const [maxBuildCoins, setMaxBuildCoins] = useState(0);
    const [buildCoinsLoading, setBuildCoinsLoading] = useState(false);
    const [showAddressModal, setShowAddressModal] = useState(false);
    const [showNewAddressModal, setShowNewAddressModal] = useState(false);
    const [userAddresses, setUserAddresses] = useState<Address[]>([]);
    const [selectedAddress, setSelectedAddress] = useState<Address | null>(null);
    const [editingAddress, setEditingAddress] = useState<Address | null>(null);
    const [addressActionLoading, setAddressActionLoading] = useState<string | null>(null);
    const [cartConfig, setCartConfig] = useState<CartConfig | null>(null);
    const [feeTooltip, setFeeTooltip] = useState<string | null>(null);
    const [showPriceChangeModal, setShowPriceChangeModal] = useState(false);
    const [priceChangeData, setPriceChangeData] = useState<{
      oldPrice: number;
      newPrice: number;
      orderId: string;
      isProceedingToConfirmation: boolean;
    } | null>(null);

    // Notify parent when selectedAddress changes
    useEffect(() => {
      if (onAddressChange) {
        onAddressChange(selectedAddress);
      }
    }, [selectedAddress, onAddressChange]);
    const [addressLoading, setAddressLoading] = useState(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<'netbanking' | 'upi' | 'cod' | null>(null);
    const [placingOrder, setPlacingOrder] = useState(false);
    const [deliveryDetails, setDeliveryDetails] = useState<{ deliveryType: 'immediate' | 'schedule'; scheduledDeliveryDate?: string; scheduledDeliveryTime?: string }>({ deliveryType: 'immediate' });
    const [showPaymentSpinner, setShowPaymentSpinner] = useState(false);
    const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);


    const syncAddresses = useCallback(async (preferredId?: string) => {
      try {
        const response = await addressService.getUserAddresses();

        if (response.success && response.data) {
          setUserAddresses(response.data);

          if (response.data.length === 0) {
            setSelectedAddress(null);
            return [];
          }

          const preferred = preferredId ? response.data.find(addr => addr._id === preferredId) : undefined;
          const existing = selectedAddress ? response.data.find(addr => addr._id === selectedAddress._id) : undefined;
          const fallback = response.data.find((addr: Address) => addr.is_default) || response.data[0];

          setSelectedAddress(preferred || existing || fallback || null);
          return response.data;
        }
      } catch (error) {
      }

      return [];
    }, [addressService, selectedAddress]);

    // Auto-switch to schedule if store is closed
    useEffect(() => {
      if (!isStoreOpen() && deliveryDetails.deliveryType === 'immediate') {
        setDeliveryDetails(prev => ({ ...prev, deliveryType: 'schedule' }));
      }
    }, []);

    // Auto-load and select first/default address on component mount
    useEffect(() => {
      if (isAuthenticated && !selectedAddress) {
        syncAddresses();
      }
    }, [isAuthenticated, selectedAddress, syncAddresses]);



    const formatPrice = (price: number) => {
      return Math.round(price).toString();
    };
    const isStoreOpen = () => {
      const now = new Date();
      const hours = now.getHours();
      return hours >= 8 && hours < 20; // 8 AM to 8 PM
    };

    const getAvailableDates = () => {
      const dates = [];
      const today = new Date();
      // include today as an option and next 6 days (7 days total)
      for (let i = 0; i < 7; i++) {
        const date = new Date(today);
        date.setDate(today.getDate() + i);
        const value = date.toISOString().split('T')[0];
        let display = date.toLocaleDateString('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric'
        });
        if (i === 0) {
          // Show only 'Today' label for the first option
          display = 'Today';
        }
        dates.push({ value, display });
      }
      return dates;
    };

    const timeSlots = [
      { value: '09:00-11:00', display: '9:00 AM - 11:00 AM' },
      { value: '11:00-13:00', display: '11:00 AM - 1:00 PM' },
      { value: '13:00-15:00', display: '1:00 PM - 3:00 PM' },
      { value: '15:00-17:00', display: '3:00 PM - 5:00 PM' },
      { value: '17:00-19:00', display: '5:00 PM - 7:00 PM' }
    ];

    const isTimeSlotPast = (slotValue: string, dateValue?: string) => {
      if (!dateValue) return false;
      try {
        const [, end] = slotValue.split('-');
        const [ey, em, ed] = dateValue.split('-').map(Number);
        const [endH, endM] = end.split(':').map(Number);
        const slotEnd = new Date(ey, em - 1, ed, endH, endM, 0);
        const now = new Date();
        return now > slotEnd;
      } catch (e) {
        return false;
      }
    };

    // Calculate total fees from config
    const totalFees = useMemo(() => {
      if (!cartConfig?.fees) return 0;
      
      return cartConfig.fees.reduce((sum, fee) => {
        if (!fee.isActive) return sum;
        
        // Check if fee should be applied based on order amount
        // If order is above maxOrderAmount threshold, fee is free
        if (fee.maxOrderAmount !== undefined && subTotal >= fee.maxOrderAmount) {
          return sum;
        }
        // If order is below minOrderAmount threshold, don't apply fee
        if (fee.minOrderAmount !== undefined && subTotal < fee.minOrderAmount) {
          return sum;
        }
        
        if (fee.type === 'fixed') {
          return sum + fee.value;
        } else if (fee.type === 'percentage') {
          return sum + (subTotal * fee.value) / 100;
        }
        return sum;
      }, 0);
    }, [cartConfig?.fees, subTotal]);

    const subtotalBeforeDiscount = subTotal + totalFees;
    const displayGrandTotal = Math.max(subtotalBeforeDiscount - discount - buildCoinsToUse, 0);

    // Get category time limit message if applicable
    const categoryTimeLimit = useMemo(() => {
      if (!cartConfig?.showCategoryTimeLimit) {
        return null;
      }
      const result = getCartCategoryTimeLimit(cartItems, cartConfig?.categoryTimeLimits);
            
      return result;
    }, [cartItems, cartConfig]);

    const handleApplyCoupon = async () => {
      if (!isAuthenticated) {
        setPromoError('Please login to apply coupons');
        return;
      }

      if (!promoCode.trim()) {
        setPromoError('Please enter a promo code');
        return;
      }

      setPromoLoading(true);
      setPromoError('');

      try {
        const apiService = new ApiService(axios.create());
        const response: any = await apiService.postRequest(
          'checkout/apply-coupon',
          {
            code: promoCode.trim(),
            orderTotal: subtotalBeforeDiscount,
          },
          {
            apiRoot: environment.api.apiRootServer,
          }
        );

        if (response.success) {
          setDiscount(response.discount || 0);
          setAppliedCoupon(response.coupon?.code || promoCode);
          setPromoError('');
        } else {
          setPromoError(response.message || 'Invalid promo code');
          setDiscount(0);
          setAppliedCoupon(null);
        }
      } catch (error: any) {
        setPromoError(error.response?.data?.message || 'Failed to apply promo code');
        setDiscount(0);
        setAppliedCoupon(null);
      } finally {
        setPromoLoading(false);
      }
    };

    const handleRemoveCoupon = () => {
      setPromoCode('');
      setDiscount(0);
      setAppliedCoupon(null);
      setPromoError('');
    };

    const handleUseAllBuildCoins = async () => {
      if (!isAuthenticated) {
        // Should not happen since build coins are only shown to authenticated users
        return;
      }

      if (buildCoinsBalance <= 0) {
        return;
      }

      setBuildCoinsLoading(true);
      try {
        const orderTotal = subtotalBeforeDiscount - discount;
        const response: any = await buildCoinsService.getAvailableCoinsForPurchase(orderTotal);

        if (response.success) {
          const coinsToUse = response.data.availableCoins;
          setBuildCoinsToUse(coinsToUse);
        }
      } catch (error) {
      } finally {
        setBuildCoinsLoading(false);
      }
    };

    const handleRemoveBuildCoins = () => {
      setBuildCoinsToUse(0);
    };

    // Address handlers
    const handleOpenAddressModal = async () => {
      if (!isAuthenticated) {
        // Redirect to auth for guests trying to checkout
        sessionStorage.setItem('fromCart', 'true');
        navigate('/auth');
        return;
      }

      setShowAddressModal(true);
      setAddressLoading(true);

      try {
        const addresses = await syncAddresses();
        if (addresses.length === 0) {
          setShowAddressModal(false);
          setShowNewAddressModal(true);
        }
      } catch (error) {
      } finally {
        setAddressLoading(false);
      }
    };

    const handleCloseAddressModal = () => {
      setShowAddressModal(false);
    };

    const handleSelectAddress = (address: Address) => {
      setSelectedAddress(address);
      setShowAddressModal(false);
    };

    const handleOpenNewAddressModal = () => {
      setEditingAddress(null);
      setShowNewAddressModal(true);
    };

    const handleCloseNewAddressModal = () => {
      setEditingAddress(null);
      setShowNewAddressModal(false);
    };

    const handleSaveAddress = async (address: Address, addressId?: string) => {
      try {
        const response = await addressService.addOrEditAddress({
          ...address,
          is_add_usecase: !addressId,
          address_id: addressId,
        });

        if (response.success) {
          const addresses = await syncAddresses(addressId);
          if (addresses.length > 0) {
            setShowAddressModal(true);
          }
        } else {
          alert('Failed to save address. Please try again.');
        }
      } catch (error) {
        alert('Failed to save address. Please try again.');
      } finally {
        setEditingAddress(null);
      }
    };

    const handleMakeDefaultAddress = async (e: MouseEvent, addressId?: string) => {
      e.stopPropagation();
      if (!addressId) return;

      setAddressActionLoading(addressId);
      try {
        await addressService.setDefaultAddress(addressId);
        await syncAddresses(addressId);
      } catch (error) {
        alert('Failed to update default address. Please try again.');
      } finally {
        setAddressActionLoading(null);
      }
    };

    const handleDeleteAddress = async (e: MouseEvent, addressId?: string) => {
      e.stopPropagation();
      if (!addressId) return;

      setAddressActionLoading(addressId);
      try {
        await addressService.deleteAddress(addressId);
        const addresses = await syncAddresses();
        if (addresses.length === 0) {
          setShowAddressModal(false);
          setShowNewAddressModal(true);
        }
      } catch (error) {
        alert('Failed to delete address. Please try again.');
      } finally {
        setAddressActionLoading(null);
      }
    };

    const handleEditAddress = (e: MouseEvent, address: Address) => {
      e.stopPropagation();
      setEditingAddress(address);
      setShowAddressModal(false);
      setShowNewAddressModal(true);
    };

    // Payment modal handlers
    const handleOpenPaymentModal = () => {
      if (!isAuthenticated) {
        // Redirect to auth for guests trying to checkout
        sessionStorage.setItem('fromCart', 'true');
        navigate('/auth');
        return;
      }

      setShowPaymentModal(true);
    };

    const handleClosePaymentModal = () => {
      setShowPaymentModal(false);
      setSelectedPaymentMethod(null);
    };

    const handleSelectPaymentMethod = (method: 'netbanking' | 'upi' | 'cod') => {
      setSelectedPaymentMethod(method);
    };

    const handleConfirmPayment = async () => {
      if (!selectedPaymentMethod) return;

      // Refetch fresh config before payment to catch any recent changes
      try {
        const configService = new CartConfigService();
        const freshConfig = await configService.getCartConfig();
        if (freshConfig) {
          setCartConfig(freshConfig);
          
          // Calculate fees with fresh config
          const freshFees = freshConfig.fees?.reduce((sum, fee) => {
            if (!fee.isActive) return sum;
            
            // Check if fee should be applied based on order amount
            if (fee.maxOrderAmount !== undefined && subTotal >= fee.maxOrderAmount) {
              return sum;
            }
            if (fee.minOrderAmount !== undefined && subTotal < fee.minOrderAmount) {
              return sum;
            }
            
            if (fee.type === 'fixed') {
              return sum + fee.value;
            } else if (fee.type === 'percentage') {
              return sum + (subTotal * fee.value) / 100;
            }
            return sum;
          }, 0) || 0;
          
          // Calculate what the total should be with fresh config
          const freshGrandTotal = Math.max(Math.round(subTotal + freshFees - discount - buildCoinsToUse), 0);
          const currentDisplayTotal = Math.round(displayGrandTotal);
          const priceDifference = Math.abs(freshGrandTotal - currentDisplayTotal);
          
          console.log('Fresh Grand Total from config:', freshGrandTotal);
          console.log('Current Display Total:', currentDisplayTotal);
          console.log('Price Difference:', priceDifference);
          
          // If prices differ by more than ₹1, alert user before payment
          if (priceDifference > 1) {
            alert(`⚠️ Price Update Detected!\n\nPrevious Total: ₹${currentDisplayTotal}\nNew Total: ₹${freshGrandTotal}\n\nPlease review your cart and try again.`);
            setIsPaymentProcessing(false);
            setShowPaymentSpinner(false);
            return;
          }
          
          // Check with fresh config
          if (!freshConfig.acceptingOrders) {
            alert(freshConfig.orderAcceptanceMessage || 'We are currently not accepting orders. Please try again later.');
            setIsPaymentProcessing(false);
            setShowPaymentSpinner(false);
            return;
          }

          if (freshConfig.minimumOrderValue && displayGrandTotal < freshConfig.minimumOrderValue) {
            alert(`Minimum order value is ₹${freshConfig.minimumOrderValue}. Your order total is ₹${Math.round(displayGrandTotal)}.`);
            setIsPaymentProcessing(false);
            setShowPaymentSpinner(false);
            return;
          }

          if (selectedPaymentMethod === 'cod' && !freshConfig.enableCODPayment) {
            alert('Cash on Delivery is currently not available. Please select another payment method.');
            setIsPaymentProcessing(false);
            setShowPaymentSpinner(false);
            return;
          }

          if ((selectedPaymentMethod === 'netbanking' || selectedPaymentMethod === 'upi') && !freshConfig.enablePrepaidPayment) {
            alert('Online payment is currently not available. Please try again later.');
            setIsPaymentProcessing(false);
            setShowPaymentSpinner(false);
            return;
          }
        }
      } catch (error) {
        console.error('Error fetching fresh config before payment:', error);
        alert('Unable to process payment. Please try again.');
        setIsPaymentProcessing(false);
        setShowPaymentSpinner(false);
        return;
      }

      // Basic validations
      if (!selectedAddress) {
        alert('Please select a delivery address');
        setIsPaymentProcessing(false);
        setShowPaymentSpinner(false);
        return;
      }

      if (selectedAddress.latitude && selectedAddress.longitude) {
        const locationService = new LocationService();
        const locationData = await locationService.reverseGeocode(
          selectedAddress.latitude,
          selectedAddress.longitude
        );

        if (!locationData.success || !locationData.data?.isServiceable) {
          toast.error(`❌ Delivery not available at this location.\n\nServiceable area: Gurugram\nYour location: ${locationData.data?.city || 'Unknown'}`, {
            position: "top-center",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
          setIsPaymentProcessing(false);
          setShowPaymentSpinner(false);
          return;
        }
      }

      setIsPaymentProcessing(true);
      setShowPaymentSpinner(true);

      // Validate scheduled delivery details if schedule is selected
      if (deliveryDetails.deliveryType === 'schedule') {
        if (!deliveryDetails.scheduledDeliveryDate) {
          alert('Please scroll down and select a delivery date');
          setIsPaymentProcessing(false);
          setShowPaymentSpinner(false);
          return;
        }
        if (!deliveryDetails.scheduledDeliveryTime) {
          alert('Please scroll down and select a delivery time slot');
          setIsPaymentProcessing(false);
          setShowPaymentSpinner(false);
          return;
        }
      }

      // For COD, create order directly
      if (selectedPaymentMethod === 'cod') {
        if (placingOrder) return;
        
        // COD limit validation
        if (displayGrandTotal > 15000) {
          alert('Cash on Delivery is not available for orders above ₹15000. Please select Online Payment to proceed.');
          setPlacingOrder(false);
          setIsPaymentProcessing(false);
          setShowPaymentSpinner(false);
          return;
        }
        
        setPlacingOrder(true);

        try {
          // Use selected address coordinates if available; do not request device geolocation
          const userLocation = {
            lat: (selectedAddress as any)?.latitude ?? (selectedAddress as any)?.lat,
            lng: (selectedAddress as any)?.longitude ?? (selectedAddress as any)?.lng,
          } as { lat?: number; lng?: number };

          const mappedItems = cartItems.map((item: any) => {
            const productId = item.productId || (item.id ? item.id.replace(/-$/, '') : undefined);
            if (!productId) return null;
            return {
              product: productId,
              quantity: item.quantity,
              price: item.price,
              variantId: item.variantId || item.variantSku,
              variantSku: item.variantSku || item.variantId,
              variantTitle: item.variantTitle,
              variantImage: item.variantImage,
              name: item.name,
              image: item.image || '',
              variantInfo: item.variantInfo || {
                name: item.name,
                sku: item.variantSku || item.variantId,
                price: item.price,
                quantity: item.quantity,
                weight: item.weight || 0,
                weightUnit: item.weightUnit || 'kg',
                isDefault: true,
                variantTitle: item.variantTitle,
                variantImage: item.variantImage,
                image: item.image,
              },
            };
          }).filter((item): item is NonNullable<typeof item> => item !== null);

          const orderPayload = {
            items: mappedItems,
            subTotal,
            shippingCost: totalFees,
            totalAmount: displayGrandTotal,
            deliveryFee: totalFees,
            grandTotal: displayGrandTotal,
            couponDiscount: discount,
            appliedCoupon: appliedCoupon,
            appliedCredits: buildCoinsToUse,
            userLocation, // Selected address coordinates
            shippingDetails: {
              address: {
                primary_address_line: selectedAddress.primary_address_line,
                secondary_address_line: selectedAddress.secondary_address_line || '',
                locality: selectedAddress.locality || '',
                landmark: selectedAddress.landmark || '',
              },
              fullName: selectedAddress.name,
              phone: selectedAddress.phone_number,
              email: '',
            },
            paymentMode: 'COD',
            deliveryType: deliveryDetails.deliveryType === 'schedule' ? 'scheduled' : 'immediate',
            scheduledDeliveryDate: deliveryDetails.scheduledDeliveryDate,
            scheduledDeliveryTime: deliveryDetails.scheduledDeliveryTime,
          };

          const checkoutService = new CheckoutService();
          const codOrderResponse = await checkoutService.createOrderRecord(orderPayload);
          const codOrderId = codOrderResponse?.data?.order?._id || codOrderResponse?.order?._id;

          // Debug: Log full response to understand structure
          console.log('COD Order Response:', codOrderResponse);
          console.log('Order details:', codOrderResponse?.data?.order || codOrderResponse?.order);

          // Check if total amount changed due to fee updates - try multiple paths
          let backendGrandTotal = codOrderResponse?.data?.order?.grandTotal || 
                                  codOrderResponse?.order?.grandTotal ||
                                  codOrderResponse?.data?.order?.totalAmount ||
                                  codOrderResponse?.order?.totalAmount ||
                                  codOrderResponse?.data?.order?.total ||
                                  codOrderResponse?.order?.total;
          
          const frontendGrandTotal = Math.round(displayGrandTotal);
          
          console.log('Frontend Grand Total:', frontendGrandTotal);
          console.log('Backend Grand Total:', backendGrandTotal);
          
          if (backendGrandTotal) {
            backendGrandTotal = Math.round(backendGrandTotal);
            const totalDifference = Math.abs(backendGrandTotal - frontendGrandTotal);
            
            console.log('Total Difference:', totalDifference);
            
            // If difference is more than ₹1 (allowing for rounding), show custom modal
            if (totalDifference > 1) {
              setPriceChangeData({
                oldPrice: frontendGrandTotal,
                newPrice: backendGrandTotal,
                orderId: codOrderId,
                isProceedingToConfirmation: false
              });
              setShowPriceChangeModal(true);
              setPlacingOrder(false);
              setIsPaymentProcessing(false);
              setShowPaymentSpinner(false);
              return;
            }
          }

          // Clear cart on backend after successful order
          if (isAuthenticated) {
            cartApiService.clearCart().catch(error => console.error('Error clearing cart after order:', error));
          }

          // Clear cart after successful order
          clearCart();
          // Set flag to prevent cart restoration on refresh
          if (typeof window !== 'undefined') {
            sessionStorage.setItem('orderPlaced', 'true');
          }

          // Close payment modal
          setShowPaymentModal(false);

          // Show spinner for 10 seconds before navigation
          setTimeout(() => {
            setShowPaymentSpinner(false);
            if (onPaymentSuccess) {
              onPaymentSuccess(codOrderId);
            } else {
              navigate('/order-confirmation', {
                state: { _id: codOrderId },
              });
            }
          },); 

        } catch (e) {
          alert('Failed to place COD order. Please try again.');
        } finally {
          setPlacingOrder(false);
        }
      } else {
        // For online payments (netbanking/upi), implement Razorpay flow
        if (placingOrder) return;
        setPlacingOrder(true);

        // Validate scheduled delivery details if schedule is selected
        if (deliveryDetails.deliveryType === 'schedule') {
          if (!deliveryDetails.scheduledDeliveryDate) {
            alert('Please scroll down and select a delivery date');
            setPlacingOrder(false);
            return;
          }
          if (!deliveryDetails.scheduledDeliveryTime) {
            alert('Please scroll down and select a delivery time slot');
            setPlacingOrder(false);
            return;
          }
        }

        try {
          // Use selected address coordinates if available; do not request device geolocation
          const userLocation = {
            lat: (selectedAddress as any)?.latitude ?? (selectedAddress as any)?.lat,
            lng: (selectedAddress as any)?.longitude ?? (selectedAddress as any)?.lng,
          } as { lat?: number; lng?: number };

          const mappedItems = cartItems.map((item: any) => {
            const productId = item.productId || (item.id ? item.id.replace(/-$/, '') : undefined);
            if (!productId) return null;
            return {
              product: productId,
              quantity: item.quantity,
              price: item.price,
              variantId: item.variantId || item.variantSku,
              variantSku: item.variantSku || item.variantId,
              variantTitle: item.variantTitle,
              variantImage: item.variantImage,
              name: item.name,
              image: item.image || '',
              variantInfo: item.variantInfo || {
                name: item.name,
                sku: item.variantSku || item.variantId,
                price: item.price,
                quantity: item.quantity,
                weight: item.weight || 0,
                weightUnit: item.weightUnit || 'kg',
                isDefault: true,
                variantTitle: item.variantTitle,
                variantImage: item.variantImage,
                image: item.image,
              },
            };
          }).filter((item): item is NonNullable<typeof item> => item !== null);

          const checkoutService = new CheckoutService();

          // Create Razorpay order with items for backend to calculate fees
          const razorpayOrder = await checkoutService.createOrder({
            items: mappedItems,
            couponCode: appliedCoupon || undefined,
            currency: 'INR',
            receipt: `order_${Date.now()}`,
            paymentMode: 'Razorpay',
          });

          // Support both response shapes: { id, amount, currency, key_id } or { order: { id, amount, currency }, key_id }
          const orderObj = (razorpayOrder && razorpayOrder.order) ? razorpayOrder.order : razorpayOrder;
          const keyId = razorpayOrder?.key_id || environment.razorpay.keyId;

          if (!orderObj?.id) {
            alert('Failed to create Razorpay order');
            setPlacingOrder(false);
            return;
          }

          // Load Razorpay SDK if needed
          const ensureRzp = () => new Promise<boolean>((resolve) => {
            if ((window as any).Razorpay) return resolve(true);
            const script = document.createElement('script');
            script.src = 'https://checkout.razorpay.com/v1/checkout.js';
            script.async = true;
            script.onload = () => resolve(true);
            script.onerror = () => resolve(false);
            document.body.appendChild(script);
          });

          const loaded = await ensureRzp();
          if (!loaded) {
            alert('Failed to load payment processor');
            setPlacingOrder(false);
            return;
          }

          const options = {
            key: keyId,
            amount: orderObj.amount,
            currency: orderObj.currency,
            name: environment.app.name,
            description: 'Order Payment',
            order_id: orderObj.id,
            handler: async (response: any) => {
              try {
                // Verify payment
                const verification = await checkoutService.verifyPayment({
                  razorpay_order_id: orderObj.id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                  order_id: orderObj.id,
                  shipping_details: {
                    fullName: selectedAddress.name,
                    phone: selectedAddress.phone_number,
                    address: {
                      primary_address_line: selectedAddress.primary_address_line,
                      secondary_address_line: selectedAddress.secondary_address_line || '',
                      locality: selectedAddress.locality || '',
                      landmark: selectedAddress.landmark || '',
                    },
                  },
                  items: mappedItems,
                  totalAmount: displayGrandTotal,
                  paymentMode: 'Razorpay',
                });

                if (!verification) {
                  alert('Payment verification failed');
                  setPlacingOrder(false);
                  return;
                }

                const orderPayload = {
                  items: mappedItems,
                  subTotal,
                  shippingCost: totalFees,
                  totalAmount: displayGrandTotal,
                  deliveryFee: totalFees,
                  grandTotal: displayGrandTotal,
                  couponDiscount: discount,
                  appliedCoupon: appliedCoupon,
                  appliedCredits: buildCoinsToUse,
                  userLocation, // Selected address coordinates
                  shippingDetails: {
                    address: {
                      primary_address_line: selectedAddress.primary_address_line,
                      secondary_address_line: selectedAddress.secondary_address_line || '',
                      locality: selectedAddress.locality || '',
                      landmark: selectedAddress.landmark || '',
                    },
                    fullName: selectedAddress.name,
                    phone: selectedAddress.phone_number,
                    email: '',
                  },
                  paymentMode: 'Razorpay',
                  deliveryType: deliveryDetails.deliveryType === 'schedule' ? 'scheduled' : 'immediate',
                  scheduledDeliveryDate: deliveryDetails.scheduledDeliveryDate,
                  scheduledDeliveryTime: deliveryDetails.scheduledDeliveryTime,
                  paymentId: response.razorpay_payment_id,
                  razorpayOrderId: response.razorpay_order_id,
                  razorpaySignature: response.razorpay_signature,
                };

                const orderResponse = await checkoutService.createOrderRecord(orderPayload);
                const orderId = orderResponse?.data?.order?._id || orderResponse?.order?._id;

                // Debug: Log full response to understand structure
                console.log('Razorpay Order Response:', orderResponse);
                console.log('Order details:', orderResponse?.data?.order || orderResponse?.order);

                // Check if total amount changed due to fee updates - try multiple paths
                let backendGrandTotal = orderResponse?.data?.order?.grandTotal || 
                                        orderResponse?.order?.grandTotal ||
                                        orderResponse?.data?.order?.totalAmount ||
                                        orderResponse?.order?.totalAmount ||
                                        orderResponse?.data?.order?.total ||
                                        orderResponse?.order?.total;
                
                const frontendGrandTotal = Math.round(displayGrandTotal);
                
                console.log('Frontend Grand Total:', frontendGrandTotal);
                console.log('Backend Grand Total:', backendGrandTotal);
                
                if (backendGrandTotal) {
                  backendGrandTotal = Math.round(backendGrandTotal);
                  const totalDifference = Math.abs(backendGrandTotal - frontendGrandTotal);
                  
                  console.log('Total Difference:', totalDifference);
                  
                  // If difference is more than ₹1 (allowing for rounding), show custom modal
                  if (totalDifference > 1) {
                    setPriceChangeData({
                      oldPrice: frontendGrandTotal,
                      newPrice: backendGrandTotal,
                      orderId: orderId,
                      isProceedingToConfirmation: false
                    });
                    setShowPriceChangeModal(true);
                    setPlacingOrder(false);
                    setShowPaymentSpinner(false);
                    return;
                  }
                }

                // Clear cart on backend after successful order
                if (isAuthenticated) {
                  cartApiService.clearCart().catch(error => console.error('Error clearing cart after order:', error));
                }

                // Clear cart after successful order
                clearCart();
                // Set flag to prevent cart restoration on refresh
                if (typeof window !== 'undefined') {
                  sessionStorage.setItem('orderPlaced', 'true');
                }

                // Close payment modal
                setShowPaymentModal(false);

                // Show spinner for 10 seconds before navigation
                setTimeout(() => {
                  setShowPaymentSpinner(false);
                  if (onPaymentSuccess) {
                    onPaymentSuccess(orderId);
                  } else {
                    navigate('/order-confirmation', {
                      state: { _id: orderId },
                    });
                  }
                },); // 10 seconds delay

              } catch (err) {
                setShowPaymentSpinner(false);
                alert('Order creation failed after payment. Please contact support.');
              } finally {
                setPlacingOrder(false);
              }
            },
            prefill: {
              name: selectedAddress.name,
              contact: selectedAddress.phone_number,
            },
            theme: { color: '#528FF0' },
            modal: {
              ondismiss: () => setPlacingOrder(false),
            },
          } as any;

          const rzp = new (window as any).Razorpay(options);
          try {
            rzp.open();
          } catch (openErr) {
            setPlacingOrder(false);
          }

          // Handle modal close
          rzp.on('modal.closed', () => {
            setPlacingOrder(false);
          });

          // Handle payment failure
          rzp.on('payment.failed', () => {
            setPlacingOrder(false);
          });

        } catch (e) {
          alert('Failed to process payment. Please try again.');
          setPlacingOrder(false);
        }
      }
    };


    // Fetch build coins balance on mount from user profile
    useEffect(() => {
      if (!isAuthenticated) return;

      const fetchBuildCoins = async () => {
        try {
          const apiService = new ApiService(axios.create());
          const profileService = new ProfileService(apiService);
          const response: any = await profileService.getUserProfile();
          if (response.success && response.user) {
            setBuildCoinsBalance(response.user.referralCredits || 0);
          }
        } catch (error) {
        }
      };
      fetchBuildCoins();
    }, [isAuthenticated]);

    // Update max build coins when order total changes
    useEffect(() => {
      const orderTotal = subtotalBeforeDiscount - discount;
      const maxCoins = Math.min(buildCoinsBalance, orderTotal * 0.5);
      setMaxBuildCoins(maxCoins);
    }, [subtotalBeforeDiscount, discount, buildCoinsBalance]);


    // Expose openPaymentModal and openAddressModal via ref
    useImperativeHandle(ref, () => ({
      openPaymentModal: handleOpenPaymentModal,
      openAddressModal: handleOpenAddressModal,
      triggerPayment: async () => {
        if (showPaymentModal && selectedPaymentMethod && !placingOrder) {
          await handleConfirmPayment();
        }
      },
    }));

    return (
      <>
        <div className="order-summary">
          <h2 className="order-summary-title">Order Summary</h2>
          {categoryTimeLimit && (
            <div style={{
              backgroundColor: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '4px',
              padding: '12px',
              marginBottom: '16px',
              color: '#856404',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{ fontSize: '16px' }}>⏱️</span>
              <span>{categoryTimeLimit.message}</span>
            </div>
          )}
          <div className="summary-section">
            <div className="summary-row">
              <span>Sub Total</span>
              <span>₹ {subTotal}</span>
            </div>
            <div className="divider"></div>
            {cartConfig?.fees && cartConfig.fees.length > 0 && cartConfig.fees.filter(f => f.isActive).map((fee) => {
              let feeAmount = 0;
              
              // Check if fee should be applied based on order amount
              // If order is above maxOrderAmount threshold, fee is free
              if (fee.maxOrderAmount !== undefined && subTotal >= fee.maxOrderAmount) {
                feeAmount = 0;
              }
              // If order is below minOrderAmount threshold, don't apply fee
              else if (fee.minOrderAmount !== undefined && subTotal < fee.minOrderAmount) {
                feeAmount = 0;
              }
              // Otherwise calculate fee normally
              else {
                if (fee.type === 'fixed') {
                  feeAmount = fee.value;
                } else if (fee.type === 'percentage') {
                  feeAmount = (subTotal * fee.value) / 100;
                }
              }

              return (
                <div key={fee._id || fee.name}>
                  <div className="summary-row">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span>{fee.name}</span>
                      {fee.description && (
                        <div style={{ position: 'relative', display: 'inline-block' }}>
                          <span
                            style={{
                              cursor: 'help',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '16px',
                              height: '16px',
                              borderRadius: '50%',
                              backgroundColor: '#e8f0fe',
                              color: '#1976d2',
                              fontSize: '12px',
                              fontWeight: 'bold',
                            }}
                            onMouseEnter={() => setFeeTooltip(fee._id || fee.name)}
                            onMouseLeave={() => setFeeTooltip(null)}
                          >
                            i
                          </span>
                          {feeTooltip === (fee._id || fee.name) && (
                            <div
                              style={{
                                position: 'absolute',
                                bottom: '125%',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                backgroundColor: '#333',
                                color: '#fff',
                                padding: '8px 12px',
                                borderRadius: '6px',
                                fontSize: '12px',
                                whiteSpace: 'nowrap',
                                zIndex: 1000,
                                marginBottom: '8px',
                              }}
                            >
                              {fee.description}
                              <div
                                style={{
                                  position: 'absolute',
                                  bottom: '-4px',
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                                  width: 0,
                                  height: 0,
                                  borderLeft: '4px solid transparent',
                                  borderRight: '4px solid transparent',
                                  borderTop: '4px solid #333',
                                }}
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <span>{feeAmount > 0 ? `₹ ${formatPrice(feeAmount)}` : 'Free'}</span>
                  </div>
                  <div className="divider"></div>
                </div>
              );
            })}
            {cartConfig?.freeShippingAbove && subTotal > 0 && subTotal < cartConfig.freeShippingAbove && (
              <>
                <div className="summary-row" style={{ color: '#0066cc', fontSize: '12px' }}>
                  <span>Free delivery on orders above ₹{cartConfig.freeShippingAbove}</span>
                </div>
                <div className="divider"></div>
              </>
            )}
            {discount > 0 && (
              <>
                <div className="divider"></div>
                <div className="summary-row" style={{ color: '#28a745' }}>
                  <span>Discount ({appliedCoupon})</span>
                  <span>- ₹ {formatPrice(discount)}</span>
                </div>
              </>
            )}
            {buildCoinsToUse > 0 && (
              <>
                <div className="divider"></div>
                <div className="summary-row" style={{ color: '#ff6b35' }}>
                  <span>Build Coins Used</span>
                  <span>- ₹ {formatPrice(buildCoinsToUse)}</span>
                </div>
              </>
            )}
            <div className="divider"></div>
            {isStoreOpen() && (
              <>
              </>
            )}
            <div className="schedule-delivery">
              <button
                type="button"
                className={`schedule-toggle ${deliveryDetails.deliveryType === 'schedule' ? 'active' : ''}`}
                onClick={() => {
                  if (isStoreOpen()) {
                    setDeliveryDetails(prev => ({ ...prev, deliveryType: prev.deliveryType === 'schedule' ? 'immediate' : 'schedule' }));
                  } else {
                    setDeliveryDetails(prev => ({ ...prev, deliveryType: 'schedule' }));
                  }
                }}
              >
                {isStoreOpen() ? 'Wanna schedule it for different time?' : 'Schedule delivery (Store is closed)'}
              </button>
              {deliveryDetails.deliveryType === 'schedule' && (!deliveryDetails.scheduledDeliveryDate || !deliveryDetails.scheduledDeliveryTime) && (
                <div style={{ marginTop: '12px', padding: '10px', backgroundColor: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: '6px', color: '#856404', fontSize: '13px', textAlign: 'center' }}>
                  <strong>Please scroll down to select your preferred delivery date and time slot.</strong>
                </div>
              )}
              {deliveryDetails.deliveryType === 'schedule' && (
                <div className="schedule-options">
                  <div className="schedule-section">
                    <label className="schedule-label">Select Date:</label>
                    <div className="date-grid">
                      {getAvailableDates().map((date) => (
                        <button
                          key={date.value}
                          type="button"
                          className={`date-option ${deliveryDetails.scheduledDeliveryDate === date.value ? 'selected' : ''}`}
                          onClick={() => setDeliveryDetails(prev => ({ ...prev, scheduledDeliveryDate: date.value }))}
                        >
                          {date.display}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="schedule-section">
                    <label className="schedule-label">Select Time Slot:</label>
                    <div className="time-grid">
                      {timeSlots.map((time) => {
                        const disabled = isTimeSlotPast(time.value, deliveryDetails.scheduledDeliveryDate);
                        return (
                          <button
                            key={time.value}
                            type="button"
                            className={`time-option ${deliveryDetails.scheduledDeliveryTime === time.value ? 'selected' : ''} ${disabled ? 'disabled' : ''}`}
                            onClick={() => {
                              if (disabled) return;
                              setDeliveryDetails(prev => ({ ...prev, scheduledDeliveryTime: time.value }));
                            }}
                            disabled={disabled}
                          >
                            {time.display}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="divider"></div>
            <div className="summary-row grand">
              <span className="grand-total">Grand Total</span>
              <span className="grand-amount">₹ {formatPrice(displayGrandTotal)}</span>
            </div>
          </div>

          <div className="promo">
            <label>Promocode</label>
            {appliedCoupon ? (
              <div className="promo-row" style={{ alignItems: 'center' }}>
                <span style={{ color: '#28a745', fontSize: '14px', fontWeight: '500' }}>
                  ✓ {appliedCoupon} applied
                </span>
                <button
                  className="apply-btn"
                  onClick={handleRemoveCoupon}
                  style={{ background: '#dc3545' }}
                >
                  Remove
                </button>
              </div>
            ) : (
              <>
                <div className="promo-row">
                  <input
                    className="promo-input"
                    placeholder="Enter Code"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                    onKeyPress={(e) => e.key === 'Enter' && handleApplyCoupon()}
                  />
                  <button
                    className="apply-btn"
                    onClick={handleApplyCoupon}
                    disabled={promoLoading}
                  >
                    {promoLoading ? 'Applying...' : 'Apply'}
                  </button>
                </div>
                {promoError && (
                  <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>
                    {promoError}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="build-coins">
            <div className="build-coins-header">
              <span className="title">Build Coins</span>
              <span className="available">Available: {formatPrice(buildCoinsBalance)} coins</span>
            </div>
            <div className="build-coins-note">
              Use your Build Coins to get up to 50% off your order
              {maxBuildCoins > 0 && ` (Max: ₹${formatPrice(maxBuildCoins)})`}
            </div>
            {buildCoinsToUse > 0 ? (
              <button
                className="build-coins-btn"
                type="button"
                onClick={handleRemoveBuildCoins}
                style={{ background: '#dc3545' }}
              >
                Remove Build Coins (₹{formatPrice(buildCoinsToUse)})
              </button>
            ) : (
              <button
                className="build-coins-btn"
                type="button"
                onClick={handleUseAllBuildCoins}
                disabled={buildCoinsLoading || buildCoinsBalance <= 0 || maxBuildCoins <= 0}
              >
                {buildCoinsLoading ? 'Applying...' : 'Use All Build Coins'}
              </button>
            )}
          </div>

          {!window.matchMedia('(max-width: 768px)').matches && (
            <div className="choose-address">
              <div className="choose-address-header">
                <span className="title">Delivery Address</span>
                {selectedAddress && (
                  <span className="selected-address">
                    {selectedAddress.label}: {selectedAddress.primary_address_line}
                    {selectedAddress.secondary_address_line && `, ${selectedAddress.secondary_address_line}`}
                    {selectedAddress.locality && `, ${selectedAddress.locality}`}
                  </span>
                )}
              </div>
              <button
                className="choose-address-btn"
                type="button"
                onClick={handleOpenAddressModal}
              >
                {selectedAddress ? 'Change Address' : 'Choose Address'}
              </button>
            </div>
          )}

        </div>

        {!hidePaymentButton && (
          <>
            {!isStoreOpen() && (
              <div style={{ backgroundColor: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: '6px', padding: '10px', marginBottom: '10px', color: '#856404', fontSize: '14px' }}>
                <strong>Note:</strong> The store is currently closed. Your order will be delivered as soon as the store opens at 8:00 AM IST.
              </div>
            )}
            <button
              className="payment-btn"
              onClick={onPaymentClick || handleOpenPaymentModal}
              disabled={isPaymentProcessing}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
            >
              {isPaymentProcessing ? (
                <>
                  <PaymentSpinner />
                  Processing...
                </>
              ) : (
                'Proceed to Payment'
              )}
            </button>
          </>
        )}

        {/* Address Selection Modal */}
        {showAddressModal && (
          <div className="address-modal-overlay" onClick={handleCloseAddressModal}>
            <div className="address-modal" onClick={(e) => e.stopPropagation()}>
              <div className="address-modal-header">
                <h3>Select Delivery Address</h3>
                <button
                  className="close-btn"
                  onClick={handleCloseAddressModal}
                >
                  ×
                </button>
              </div>
              <div className="address-modal-body">
                {addressLoading ? (
                  <div className="loading">Loading addresses...</div>
                ) : userAddresses.length > 0 ? (
                  <div className="address-list">
                    {userAddresses.map((address) => (
                      <div
                        key={address._id}
                        className={`address-item ${selectedAddress?._id === address._id ? 'selected' : ''}`}
                        onClick={() => handleSelectAddress(address)}
                      >
                        <div className="address-label">{address.label}</div>
                        <div className="address-details">
                          <div className="address-name">{address.name}</div>
                          <div className="address-text">
                            {address.primary_address_line}
                            {address.secondary_address_line && `, ${address.secondary_address_line}`}
                            {address.locality && `, ${address.locality}`}
                            {address.landmark && `, ${address.landmark}`}
                          </div>
                        </div>
                        <div className="address-actions">
                          {!address.is_default && (
                            <button
                              className="address-action-btn primary"
                              onClick={(e) => handleMakeDefaultAddress(e, address._id)}
                              disabled={addressActionLoading === address._id || !address._id}
                              type="button"
                            >
                              {addressActionLoading === address._id ? 'Saving...' : 'Make Default'}
                            </button>
                          )}
                          <button
                            className="address-action-btn"
                            onClick={(e) => handleEditAddress(e, address)}
                            disabled={addressActionLoading === address._id || !address._id}
                            type="button"
                          >
                            Edit
                          </button>
                          <button
                            className="address-action-btn danger"
                            onClick={(e) => handleDeleteAddress(e, address._id)}
                            disabled={addressActionLoading === address._id || !address._id}
                            type="button"
                          >
                            {addressActionLoading === address._id ? 'Removing...' : 'Delete'}
                          </button>
                        </div>
                        {address.is_default && (
                          <div className="default-badge">Default</div>
                        )}
                      </div>
                    ))}
                    {/* Add New Address Item */}
                    <div
                      className="address-item add-new-address"
                      onClick={() => {
                        setShowAddressModal(false);
                        handleOpenNewAddressModal();
                      }}
                    >
                      <div className="add-address-content">
                        <div className="add-icon">+</div>
                        <div className="add-text">Add New Address</div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="no-addresses">No addresses found. Please add an address in your profile.</div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* New Address Modal */}
        <NewAddressModal
          isOpen={showNewAddressModal}
          onClose={handleCloseNewAddressModal}
          onAddAddress={handleSaveAddress}
          addressToEdit={editingAddress}
        />

        {/* Payment Method Modal */}
        {showPaymentModal && (
          <div className="payment-modal-overlay" onClick={handleClosePaymentModal}>
            <div className="payment-modal" onClick={(e) => e.stopPropagation()}>
              <div className="payment-modal-header">
                <h3>Select Payment Method</h3>
                <button
                  className="close-btn"
                  onClick={handleClosePaymentModal}
                >
                  ×
                </button>
              </div>
              <div className="payment-modal-body">
                <div className="payment-options">
                  <div
                    className={`payment-option ${selectedPaymentMethod === 'upi' ? 'selected' : ''} ${placingOrder ? 'disabled' : ''}`}
                    onClick={() => !placingOrder && handleSelectPaymentMethod('upi')}
                  >
                    <div className="payment-details">
                      <div className="payment-title">Online Payment</div>
                      <div className="payment-description">Save the hassle by paying online</div>
                    </div>
                    <div className={`payment-radio ${selectedPaymentMethod === 'upi' ? 'checked' : ''}`}></div>
                  </div>

                  <div
                    className={`payment-option ${selectedPaymentMethod === 'cod' ? 'selected' : ''} ${placingOrder ? 'disabled' : ''}`}
                    onClick={() => !placingOrder && handleSelectPaymentMethod('cod')}
                  >
                    <div className="payment-details">
                      <div className="payment-title">Cash on Delivery</div>
                      <div className="payment-description">Pay when you receive your order</div>
                    </div>
                    <div className={`payment-radio ${selectedPaymentMethod === 'cod' ? 'checked' : ''}`}></div>
                  </div>
                  {selectedPaymentMethod === 'cod' && displayGrandTotal > 15000 && (
                    <div style={{
                      marginTop: '16px',
                      padding: '12px',
                      backgroundColor: '#fff3cd',
                      border: '1px solid #ffc107',
                      borderRadius: '6px',
                      color: '#856404',
                      fontSize: '13px',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '8px'
                    }}>
                      <span style={{ fontSize: '16px', flexShrink: 0 }}>⚠️</span>
                      <div>
                        <strong>COD Not Available</strong>
                        <div style={{ fontSize: '12px', marginTop: '4px' }}>
                          Cash on Delivery is only available for orders up to ₹15000. Your order total is ₹{displayGrandTotal.toFixed(2)}. Please select Online Payment.
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="payment-modal-actions">
                  <button
                    className="cancel-btn"
                    onClick={handleClosePaymentModal}
                    disabled={placingOrder}
                  >
                    Cancel
                  </button>
                  <button
                    className="confirm-btn"
                    onClick={handleConfirmPayment}
                    disabled={!selectedPaymentMethod || placingOrder}
                  >
                    {placingOrder ? 'Placing Order...' : 'Continue to Payment'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showPaymentSpinner && (
          <div className="payment-spinner-overlay">
            <PaymentSpinner />
          </div>
        )}
        {showPaymentSpinner && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              padding: '20px',
              borderRadius: '8px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '10px'
            }}>
              <PaymentSpinner />
              <div>Processing your payment...</div>
            </div>
          </div>
        )}

        {showPriceChangeModal && priceChangeData && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 2000
          }}>
            <div style={{
              backgroundColor: 'white',
              padding: '24px',
              borderRadius: '12px',
              maxWidth: '400px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              textAlign: 'center'
            }}>
              <h3 style={{ marginBottom: '16px', color: '#d32f2f' }}>Price Updated</h3>
              <p style={{ marginBottom: '16px', fontSize: '14px', color: '#666' }}>
                The order total has changed due to updated fees/charges:
              </p>
              <div style={{
                backgroundColor: '#f5f5f5',
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '16px',
                fontSize: '14px'
              }}>
                <div style={{ marginBottom: '8px' }}>
                  <strong>Previous Total:</strong> ₹{priceChangeData.oldPrice}
                </div>
                <div style={{
                  fontSize: '12px',
                  color: '#999'
                }}>
                  ↓
                </div>
                <div style={{ marginTop: '8px' }}>
                  <strong style={{ color: '#d32f2f' }}>New Total:</strong> ₹{priceChangeData.newPrice}
                </div>
              </div>
              <p style={{ marginBottom: '20px', fontSize: '12px', color: '#999' }}>
                Order ID: {priceChangeData.orderId}
              </p>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                <button
                  onClick={() => {
                    setShowPriceChangeModal(false);
                    setPriceChangeData(null);
                    // Refresh config and go back to cart
                    const configService = new CartConfigService();
                    configService.getCartConfig().then(config => {
                      setCartConfig(config);
                    });
                  }}
                  style={{
                    padding: '10px 16px',
                    border: '1px solid #ccc',
                    borderRadius: '6px',
                    backgroundColor: '#f5f5f5',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Recheck Cart
                </button>
                <button
                  onClick={() => {
                    setShowPriceChangeModal(false);
                    setPriceChangeData(null);
                    clearCart();
                    if (typeof window !== 'undefined') {
                      sessionStorage.setItem('orderPlaced', 'true');
                    }
                    setShowPaymentModal(false);
                    setTimeout(() => {
                      if (onPaymentSuccess) {
                        onPaymentSuccess(priceChangeData.orderId);
                      } else {
                        navigate('/order-confirmation', {
                          state: { _id: priceChangeData.orderId },
                        });
                      }
                    }, 500);
                  }}
                  style={{
                    padding: '10px 16px',
                    border: 'none',
                    borderRadius: '6px',
                    backgroundColor: '#4CAF50',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Proceed
                </button>
              </div>
            </div>
          </div>
        )}

      </>
    );
  });

export default CartOrderSummary;
